import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { version } from '../package.json';
import appConfig from './config/app.config';
import ErrorPage from './pages/error/ErrorPage';
import HomePage from './pages/home/HomePage';
import LoginPage from './pages/login/LoginPage';
import TenantWorkOrderFormPage from './pages/tenant/work-order-form/TenantWorkOrderFormPage';
import useAuth from './hooks/useAuth';
import OnboardingPage from './pages/onboarding/OnboardingPage';
import ResponsiveDrawer from './components/navigation/ResponsiveDrawer';
import Loader from './components/common/Loader';
import WorkOrderDetailsPage from './pages/work-order/WorkOrderDetailsPage';
import CreateWorkOrderPage from './pages/create-work-order/CreateWorkOrderPage';
import CreateWorkOrderFormPage from './pages/create-work-order-form/CreateWorkOrderFormPage';
import { PropertiesPage } from './pages/properties/PropertiesPage';
import { PropertyDetailsPage } from './pages/property-details/PropertyDetailsPage';
import SettingsPage from './pages/settings/SettingsPage';
import UsersPage from './pages/users/UsersPage';
import UserDetailsPage from './pages/user-details/UserDetailsPage';
import ProcureVendorsPage from './pages/procure-vendors/ProcureVendorsPage';
import ProcureVendorDetailsPage from './pages/procure-vendor-details/ProcureVendorDetailsPage';
import CreateVendorPage from './pages/create-vendor/CreateVendorPage';
import AppListingSnackbar from './components/navigation/AppListingSnackbar';
import RedirectWorkOrderDetails from './pages/work-order/RedirectWorkOrderDetails';
import useConfigurations from './hooks/useConfigurations';
import { Configuration, CONFIGURATION_TYPE } from 'vendor-marketplace-shared-library';
import { getConfigurationValue } from './utilities/helpers';

let browserRouter = createBrowserRouter;
if (appConfig.isLiveEnvironment()) {
  Sentry.init({
    dsn: 'https://69270a302b90493a90b6787194f33044@o1109446.ingest.sentry.io/4505398950887424',
    environment: appConfig.environment,
    release: version,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        ),
      }),
    ],
    tracesSampleRate: 1.0,
    debug: false,
  });

  browserRouter = Sentry.wrapCreateBrowserRouter(browserRouter);
}

export default function Router() {
  const { accessToken, userQuery, isOnboarded, loading } = useAuth();
  const { configurationsQuery } = useConfigurations();

  const getAppCtaInMobileBrowserisEnabledConfig =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.GET_APP_CTA_IN_MOBILE_BROWSER_IS_ENABLED,
    );

  const isGetAppCtaInMobileBrowserisEnabled = getConfigurationValue(
    getAppCtaInMobileBrowserisEnabledConfig,
  );

  const userConciergeVendorsIsEnabledConfiguration =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.CONCIERGE_VENDORS_IS_ENABLED,
    );

  const isUserConciergeVendorsEnabled = getConfigurationValue(
    userConciergeVendorsIsEnabledConfiguration,
  );

  if (loading || (accessToken && userQuery.loading)) {
    return <Loader />;
  }

  if (!accessToken) {
    return (
      <>
        {isGetAppCtaInMobileBrowserisEnabled && <AppListingSnackbar />}
        <Routes>
          <Route path='/tenant/work-order-form/:hash' element={<TenantWorkOrderFormPage />} />
          <Route path='/login' element={<LoginPage />} />
          <Route path='*' element={<Navigate to='/login' replace />} />
        </Routes>
      </>
    );
  } else if (!isOnboarded) {
    return (
      <>
        {isGetAppCtaInMobileBrowserisEnabled && <AppListingSnackbar />}
        <Routes>
          <Route path='/' element={<OnboardingPage />} />
          <Route path='*' element={<Navigate to='/' replace />} />
        </Routes>
      </>
    );
  } else {
    return (
      <ResponsiveDrawer>
        {isGetAppCtaInMobileBrowserisEnabled && <AppListingSnackbar />}
        <Routes>
          <Route path='/' element={<HomePage />} />
          <Route path='/work-orders' element={<RedirectWorkOrderDetails />} />
          <Route path='/work-orders/:id' element={<WorkOrderDetailsPage />} />
          <Route path='/create-work-order' element={<CreateWorkOrderPage />} />
          <Route path='/create-work-order/:id' element={<CreateWorkOrderFormPage />} />
          <Route path='/users' element={<UsersPage />} />
          <Route path='/users/:id' element={<UserDetailsPage />} />
          {isUserConciergeVendorsEnabled && (
            <Route path='/vendors' element={<ProcureVendorsPage />} />
          )}
          {isUserConciergeVendorsEnabled && (
            <Route path='/vendors/:id' element={<ProcureVendorDetailsPage />} />
          )}
          {isUserConciergeVendorsEnabled && (
            <Route path='/vendors/create' element={<CreateVendorPage />} />
          )}
          <Route path='/properties' element={<PropertiesPage />} />
          <Route path='/properties/:id' element={<PropertyDetailsPage />} />
          <Route path='/settings' element={<SettingsPage />} />
          <Route path='/tenant/work-order-form/:hash' element={<TenantWorkOrderFormPage />} />
          <Route path='/404' element={<ErrorPage />} />
          <Route path='*' element={<Navigate to='/404' replace />} />
        </Routes>
      </ResponsiveDrawer>
    );
  }
}
